import styled from "styled-components";

export const PageContainer = styled.div`
  padding: 24px;
  background: #fff;
  min-height: 100vh;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
  }

  .header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;

  .filter-item {
    min-width: 200px;
    flex: 1;
    max-width: 300px;

    .filter-label {
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      color: #666;
      font-weight: 500;
    }
  }

  .filter-actions {
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }
`;

export const TableContainer = styled.div`
  .ant-table-wrapper {
    .ant-table {
      border-radius: 8px;
      overflow: hidden;
    }

    .ant-table-thead > tr > th {
      background: #fafafa;
      font-weight: 600;
    }

    .ant-table-row {
      &:hover {
        background: #f5f5f5;
      }
    }
  }
`;

export const StatusTag = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => props.$color}20;
  color: ${(props) => props.$color};
  border: 1px solid ${(props) => props.$color}40;
`;

export const BackButton = styled.div`
  margin-bottom: 16px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #1890ff;
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Stats Dashboard
export const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

export const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(
    135deg,
    ${(props) => props.$color || "#6366f1"}15 0%,
    ${(props) => props.$color || "#6366f1"}05 100%
  );
  border: 1px solid ${(props) => props.$color || "#6366f1"}30;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.$color || "#6366f1"}20;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${(props) => props.$color || "#6366f1"}20;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    color: ${(props) => props.$color || "#6366f1"};
    font-size: 18px;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 4px;
    line-height: 1;
  }

  .stat-label {
    font-size: 13px;
    color: #666;
    font-weight: 500;
  }
`;

// Progress Timeline - same pattern as EInvoiceBatches
export const TimelineContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px;
  background: #fafafa;
  border-radius: 12px;
  margin-bottom: 24px;
  position: relative;
  overflow-x: auto;

  /* The connector runs through the middle of the icons */
  &::before {
    content: "";
    position: absolute;
    top: 47px;
    left: 80px;
    right: 80px;
    height: 3px;
    background: #e5e7eb;
    z-index: 0;
  }
`;

export const TimelineStep = styled.div<{
  $active: boolean;
  $completed: boolean;
  $failed?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
  background: #fafafa;
  padding: 0 12px;
  flex: 0 1 auto;
  min-width: 96px;
  max-width: 140px;
  text-align: center;

  .step-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 8px;
    transition: all 0.3s ease;

    background: ${(props) => {
      if (props.$failed) return "#fff2f0";
      if (props.$completed) return "#f6ffed";
      if (props.$active) return "#e6f7ff";
      return "#f5f5f5";
    }};

    border: 2px solid
      ${(props) => {
        if (props.$failed) return "#ff4d4f";
        if (props.$completed) return "#52c41a";
        if (props.$active) return "#1890ff";
        return "#d9d9d9";
      }};

    color: ${(props) => {
      if (props.$failed) return "#ff4d4f";
      if (props.$completed) return "#52c41a";
      if (props.$active) return "#1890ff";
      return "#8c8c8c";
    }};
  }

  .step-label {
    font-size: 12px;
    font-weight: 500;
    color: ${(props) => {
      if (props.$failed) return "#ff4d4f";
      if (props.$completed || props.$active) return "#1a1a2e";
      return "#8c8c8c";
    }};
  }

  .step-time {
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => {
      if (props.$failed) return "#ff4d4f";
      if (props.$completed || props.$active) return "#1a1a2e";
      return "#8c8c8c";
    }};
    margin-top: 4px;
  }

  .step-date {
    font-size: 11px;
    color: #8c8c8c;
    margin-top: 2px;
  }
`;

export const ClaimSummaryCard = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;

  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
  }

  .summary-divider {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    margin: 8px 0;

    span {
      font-size: 13px;
      font-weight: 600;
      color: #8c8c8c;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;
      padding-right: 12px;
    }

    &::after {
      content: "";
      flex: 1;
      height: 1px;
      background: #e8e8e8;
    }
  }

  .summary-item {
    padding: 16px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #f0f0f0;

    &.highlight {
      background: linear-gradient(135deg, #f6ffed 0%, #fff 100%);
      border-color: #b7eb8f;
    }

    .summary-label {
      font-size: 12px;
      color: #666;
      margin-bottom: 4px;
    }

    .summary-value {
      font-size: 24px;
      font-weight: 600;
      color: #1890ff;

      &.success {
        color: #52c41a;
      }

      &.error {
        color: #ff4d4f;
      }

      &.warning {
        color: #faad14;
      }
    }
  }
`;

export const InvoicesSection = styled.div`
  margin-top: 24px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  }
`;

export const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ModalContent = styled.div`
  .form-item {
    margin-bottom: 16px;

    label {
      display: block;
      margin-bottom: 4px;
      font-weight: 500;
    }
  }

  .form-row {
    display: flex;
    gap: 16px;

    .form-item {
      flex: 1;
    }
  }
`;

export const MoneyValue = styled.span<{ $variant?: "success" | "warning" | "primary" | "default" }>`
  display: inline-flex;
  align-items: baseline;
  font-weight: 600;

  .currency {
    font-size: 0.7em;
    margin-left: 4px;
    opacity: 0.8;
  }

  .amount {
    font-family: "Roboto Mono", monospace;
  }

  color: ${(props) => {
    switch (props.$variant) {
      case "success":
        return "#52c41a";
      case "warning":
        return "#faad14";
      case "primary":
        return "#1890ff";
      default:
        return "inherit";
    }
  }};
`;
